import { useState } from 'react';
import { components as cmsComponents } from '@lifeserver/models/cms';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios, { AxiosResponse, isAxiosError } from 'axios';
import _ from 'lodash';

import { PACKS_BASE_PATH } from '../utils/pathsConstants';
import { handleError } from '../utils/utils';

import { Banner } from '../types/bannerTypes';
import { Carousel } from '../types/carouselTypes';
import { Pack, PackListItem, PackUpdate, PriceLevel } from '../types/packTypes';
import { Tab } from '../types/tabTypes';

const QUERY_KEY_PACKS = 'packs';

type UsePackOptions = {
    basepath?: string;
};

export function usePack(packId: string, options: UsePackOptions = {}) {
    const { basepath } = options;
    const [pack, setPack] = useState<Pack | undefined>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>();
    const [errorStatus, setErrorStatus] = useState<number | undefined>();

    async function fetchPack() {
        setPack(undefined);
        setIsLoading(true);
        setError(undefined);
        setErrorStatus(undefined);

        try {
            const url = basepath
                ? `${basepath}/${PACKS_BASE_PATH}/${packId}`
                : `/api/${PACKS_BASE_PATH}/${packId}`;
            const {
                data: { pack },
            } = await axios.get(url);
            setPack(pack);
        } catch (err) {
            const message = handleError('Fetch pack error', err, false);

            if (isAxiosError(err)) {
                setErrorStatus(err.response?.status);
            }

            setError(message);
        }

        setIsLoading(false);
    }

    return {
        pack,
        fetch: fetchPack,
        isLoading,
        error,
        errorStatus,
    };
}

type UsePacksOptions = {
    basepath?: string;
    enabled?: boolean;
};

async function listPacks(basepath?: string) {
    const url = basepath ? `${basepath}/store/v2/packs` : '/api/store/v2/packs';

    const { data } = await axios.get<{ packs: Pack[] }>(url);

    return data;
}

export function usePacks(options: UsePacksOptions = {}) {
    const { basepath, enabled: isEnabled = false } = options;

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: [QUERY_KEY_PACKS, basepath],
        queryFn: () => listPacks(basepath),
        enabled: isEnabled,
    });

    return {
        packs: data?.packs,
        isLoading,
        error: error
            ? handleError('Fetch packs error', error, false)
            : undefined,
        fetch: refetch,
    };
}

const QUERY_KEY_PACKS_LIST = 'packs-list';

async function listPacksList(basepath?: string) {
    const url = basepath
        ? `${basepath}/${PACKS_BASE_PATH}/list/all`
        : `/api/${PACKS_BASE_PATH}/list/all`;

    const { data } = await axios.get<{ packs: PackListItem[] }>(url);

    return data;
}

export function usePacksList(options: UsePacksOptions = {}) {
    const { basepath, enabled: isEnabled = false } = options;

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: [QUERY_KEY_PACKS_LIST, basepath],
        queryFn: () => listPacksList(basepath),
        enabled: isEnabled,
    });

    return {
        packs: data?.packs,
        isLoading,
        error: error
            ? handleError('Fetch packs list error', error, false)
            : undefined,
        fetch: refetch,
    };
}

type UseCreatePackOptions = {
    basepath?: string;
};

export function useCreatePack(options: UseCreatePackOptions = {}) {
    const { basepath } = options;
    const [createdPack, setCreatedPack] = useState<Pack | undefined>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>();

    async function createPack(pack: PackUpdate) {
        setCreatedPack(undefined);
        setIsLoading(true);

        try {
            const url = basepath
                ? `${basepath}/store/v2/pack`
                : '/api/store/v2/pack';
            const { data } = await axios.post(url, { pack });
            setCreatedPack(data);
        } catch (err) {
            setError(handleError('Create pack error', err, false));
        }

        setIsLoading(false);
    }

    return {
        createPack,
        pack: createdPack,
        isLoading,
        error,
    };
}

type UseUpdatePackOptions = {
    basepath?: string;
};

export function useUpdatePack(options: UseUpdatePackOptions = {}) {
    const { basepath } = options;
    const [updatedPack, setUpdatedPack] = useState<Pack | undefined>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>();

    async function updatePack(pack: PackUpdate) {
        setUpdatedPack(undefined);
        setIsLoading(true);

        try {
            const url = basepath
                ? `${basepath}/store/v2/pack/${pack.packId}`
                : `/api/store/v2/pack/${pack.packId}`;
            const { data } = await axios.put(url, { pack });
            setUpdatedPack(data);
        } catch (err) {
            setError(handleError('Update pack error', err, false));
        }

        setIsLoading(false);
    }

    return {
        updatePack,
        pack: updatedPack,
        isLoading,
        error,
    };
}

export function usePackMembership(packId: string) {
    const [membership, setMembership] = useState<{
        packs: Pack[];
        carousels: Carousel[];
        tabs: Tab[];
        banners: Banner[];
    }>({
        packs: [],
        carousels: [],
        tabs: [],
        banners: [],
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>();

    async function fetchPackMembership() {
        setIsLoading(true);
        setMembership({ packs: [], carousels: [], tabs: [], banners: [] });
        setError(undefined);

        try {
            const { data } = await axios.get(
                `/api/${PACKS_BASE_PATH}/${packId}/usages`,
            );

            setMembership(data);
        } catch (error) {
            setError(handleError('Fetch pack membership error', error, false));
        }

        setIsLoading(false);
    }

    return {
        membership,
        fetch: fetchPackMembership,
        isLoading,
        error,
    };
}

export type PackDescriptionType = {
    displayName: Pack['displayName'];
    features: Pack['features'];
};

export function useDescriptionFromGoogleSheet(packName: string) {
    const [description, setDescription] = useState<PackDescriptionType | null>(
        null,
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>();

    const loadGoogleSheetData = async () => {
        try {
            setError(undefined);
            setIsLoading(true);

            const {
                data,
            }: AxiosResponse<{
                name: Pack['displayName'];
                wyg: Pack['features'];
            }> = await axios.get(
                `/api/${PACKS_BASE_PATH}/getpackfromsource/${packName}`,
            );

            // Everything went well, but there are no translations from Google sheet for this String ID
            if (_.isEmpty(data.name)) {
                throw new Error(
                    'There are no translations for this String ID!',
                );
            }

            setDescription({
                displayName: data.name,
                features: data.wyg || {},
            });
        } catch (error) {
            const errorMessage = handleError(
                'Could not load pack description from the spreadsheet',
                error,
                false,
            );

            setDescription(null);
            setError(errorMessage);
        }

        setIsLoading(false);
    };

    return {
        description,
        load: loadGoogleSheetData,
        isLoading: isLoading,
        error,
    };
}

export function usePriceLevelsFromGoogleSheet(packId: Pack['packId']) {
    const [priceLevels, setPriceLevels] = useState<PriceLevel[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>();

    const loadGoogleSheetData = async () => {
        try {
            setError(undefined);
            setIsLoading(true);

            const { data }: AxiosResponse<PriceLevel[]> = await axios.get(
                `/api/${PACKS_BASE_PATH}/getspricelevelsfromsource/${packId}`,
            );

            if (_.isEmpty(data)) {
                throw new Error('There are no price levels for this Pack!');
            }

            setPriceLevels(data);
        } catch (error) {
            const errorMessage = handleError(
                'Could not load pack price levels from the spreadsheet',
                error,
                false,
            );

            setPriceLevels([]);
            setError(errorMessage);
        }

        setIsLoading(false);
    };

    return {
        priceLevels,
        load: loadGoogleSheetData,
        isLoading: isLoading,
        error,
    };
}

export type PackContentCounts = cmsComponents['schemas']['PackContentCounts'];

export function useContentFromGoogleSheet() {
    const [content, setContent] = useState<PackContentCounts | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>();

    const loadGoogleSheetData = async (displayName: string) => {
        try {
            setError(undefined);
            setIsLoading(true);

            const { data }: AxiosResponse<PackContentCounts> = await axios.get(
                `/api/${PACKS_BASE_PATH}/content/from-source?displayName=${encodeURIComponent(displayName)}`,
            );

            setContent(data);
        } catch (error) {
            const errorMessage = handleError(
                'Could not load pack content from the spreadsheet',
                error,
                false,
            );

            setContent(null);
            setError(errorMessage);
        }

        setIsLoading(false);
    };

    return {
        content,
        load: loadGoogleSheetData,
        isLoading,
        error,
    };
}

export type IapExportResult = {
    productId: string;
    url?: string;
    error: string | null;
    skipped?: boolean;
};

export enum IapExportStatus {
    Exporting,
    Exported,
    PartiallyExported,
    Failed,
    NotSet,
}

export function useIapExportToApple() {
    const [status, setStatus] = useState<IapExportStatus>(
        IapExportStatus.NotSet,
    );
    const [error, setError] = useState<string | undefined>();
    const [results, setResults] = useState<IapExportResult[]>([]);

    const exportIap = async (packId: Pack['packId'], ids: string[] = []) => {
        try {
            setError(undefined);
            setStatus(IapExportStatus.Exporting);

            const response = await axios.post<IapExportResult[]>(
                `/api/v2/pack/${packId}/export/applestore`,
                { productIds: ids },
            );

            setResults(response.data);
            setStatus(IapExportStatus.Exported);
        } catch (e) {
            const message = handleError(
                'Failed to send data to the Apple store',
                e,
                false,
            );
            setError(message);
            setStatus(IapExportStatus.Failed);
        }
        setTimeout(() => {
            setStatus(IapExportStatus.NotSet);
        }, 5000);
    };

    return {
        status,
        exportIap,
        error,
        results,
    };
}

export function useIapExportToGoogle() {
    const [status, setStatus] = useState<IapExportStatus>(
        IapExportStatus.NotSet,
    );
    const [error, setError] = useState<string | undefined>();
    const [results, setResults] = useState<IapExportResult[]>([]);

    const exportIap = async (packId: Pack['packId'], ids: string[] = []) => {
        try {
            setError(undefined);
            setStatus(IapExportStatus.Exporting);

            const response = await axios.post<IapExportResult[]>(
                `/api/v2/pack/${packId}/export/googleplay`,
                { productIds: ids },
            );

            setResults(response.data);
            setStatus(IapExportStatus.Exported);
        } catch (e) {
            const message = handleError(
                'Failed to send data to the Google Play store',
                e,
                false,
            );
            setError(message);
            setStatus(IapExportStatus.Failed);
        }
        setTimeout(() => {
            setStatus(IapExportStatus.NotSet);
        }, 5000);
    };

    return {
        status,
        exportIap,
        error,
        results,
    };
}

export function useExportToXsolla() {
    return useMutation({
        mutationFn: (packId: Pack['packId']) =>
            axios.post(`/api/v2/pack/${packId}/export/xsolla`),
    });
}
